import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { GlassButtonWell } from 'features/knowledgeDocumentViewer/components/viewer/shared/glassSurface'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { ExternalLink, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { keyframes, styled } from 'styled-components'
import { PHONE_LANDSCAPE_QUERY } from 'utils/mobileLayout'

import { DocumentPreviewContent } from './DocumentPreviewContent'

const pulse = keyframes`
  50% { opacity: 0.5; }
`

const SkeletonBase = styled.div`
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  border-radius: 0.25rem;
  background-color: var(--muted);
`

const SkeletonTitle = styled(SkeletonBase)`
  height: 1rem;
  width: 60%;
`

const SkeletonThumbnail = styled(SkeletonBase)`
  height: 55%;
  width: 100%;
`

const SkeletonMetaLabel = styled(SkeletonBase)`
  height: 0.625rem;
  width: 40%;
`

const SkeletonMetaValue = styled(SkeletonBase)`
  height: 0.75rem;
  width: 75%;
`

const PanelRoot = styled(Box)`
  position: relative;
`

const CloseButtonWell = styled(GlassButtonWell)`
  position: absolute;
  z-index: 2;
  top: 0.5rem;
  right: 0.5rem;
`

const SkeletonTitleSection = styled.div`
  padding: 1.25rem 1.5rem;
`

const SkeletonMetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
`

const SkeletonMetaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(4.75rem, 30%) minmax(0, 1fr);
  gap: 0.75rem;
`

const ActionFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 1rem;
  border-top: 1px solid var(--border);
  background: ${({ theme }) => theme.forecast.pageBackground1};

  @media ${PHONE_LANDSCAPE_QUERY} {
    padding: 0.5rem 1rem;
  }
`

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`

export interface DocumentPreviewPanelProps {
  document: DocumentTableRow | null
  isOpen: boolean
  isLinkedDocsMode: boolean
  isLoading?: boolean
  onClose: () => void
  onDocumentNavigate: (
    doc: DocumentTableRow,
    openInNewTab: boolean,
    isLinkedDocsMode: boolean,
    target?: {
      pageNumber?: number
      pageNum?: number
      fragmentText?: string
      citedQuotes?: string[]
    }
  ) => void
  onSwitchPreviewDocument?: (doc: DocumentTableRow, pageNum: number) => void
  onAddToContext?: (doc: DocumentTableRow) => void
  initialPage?: number
  citedQuotes?: string[]
  onDismissCitedQuotes?: () => void
  panelBorder?: 'left' | 'none'
}

export function DocumentPreviewPanel({
  document,
  isOpen,
  isLoading = false,
  onClose,
  onDocumentNavigate,
  onSwitchPreviewDocument,
  isLinkedDocsMode,
  initialPage = 1,
  citedQuotes,
  onDismissCitedQuotes,
  panelBorder = 'left',
}: DocumentPreviewPanelProps) {
  const { t } = useLingui()
  const documentUX = useKnowledgeDocumentUXContextOptional()
  const canViewUserEdits = documentUX?.canViewUserEdits ?? false
  const canEditUserEdits = documentUX?.canEditUserEdits ?? false
  const isGeckoUser = documentUX?.isGeckoUser ?? false
  const [isMetadataEditing, setIsMetadataEditing] = useState(false)

  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onClose()
  }

  const handleMetadataEditingChange = useCallback((isEditing: boolean) => {
    setIsMetadataEditing(isEditing)
  }, [])

  const handleOpenDocumentInNewTab = useCallback(
    (doc: DocumentTableRow, pageNum: number) => {
      onDocumentNavigate(doc, true, isLinkedDocsMode, { pageNumber: pageNum })
    },
    [isLinkedDocsMode, onDocumentNavigate]
  )

  useEffect(() => {
    setIsMetadataEditing(false)
  }, [document?.id])

  if (!isOpen) {
    return null
  }

  if (isLoading || !document) {
    return (
      <PanelRoot
        display='flex'
        direction='column'
        height='full'
        minHeight='none'
        width='full'
        minWidth='none'
        border={panelBorder}
        surface='transparent'
        overflow='hidden'>
        <CloseButtonWell>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={handleCloseClick}
            aria-label={t`Close preview`}>
            <X />
          </Button>
        </CloseButtonWell>
        <Box
          display='flex'
          direction='column'
          flex='one'
          minHeight='none'
          overflow='hidden'>
          <SkeletonThumbnail />
          <SkeletonTitleSection>
            <SkeletonTitle />
          </SkeletonTitleSection>
          <SkeletonMetaList>
            {Array.from({ length: 5 }, (_, i) => (
              <SkeletonMetaRow key={i}>
                <SkeletonMetaLabel />
                <SkeletonMetaValue />
              </SkeletonMetaRow>
            ))}
          </SkeletonMetaList>
        </Box>
        <ActionFooter>
          <ActionButtons>
            <Button variant='primaryRaised' size='lg' disabled>
              <Trans>Open</Trans>
            </Button>
            <Button
              variant='inputRaised'
              size='lg'
              disabled
              title={t`Open in new tab`}>
              <Trans>Open In New Tab</Trans>
              <ExternalLink />
            </Button>
          </ActionButtons>
        </ActionFooter>
      </PanelRoot>
    )
  }

  return (
    <PanelRoot
      display='flex'
      direction='column'
      height='full'
      minHeight='none'
      width='full'
      minWidth='none'
      border={panelBorder}
      surface='transparent'
      overflow='hidden'>
      <CloseButtonWell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={handleCloseClick}
          aria-label={t`Close preview`}>
          <X />
        </Button>
      </CloseButtonWell>

      <Box
        display='flex'
        direction='column'
        flex='one'
        minHeight='none'
        overflow='hidden'>
        <DocumentPreviewContent
          document={document}
          initialPage={initialPage}
          citedQuotes={citedQuotes}
          onDismissCitedQuotes={onDismissCitedQuotes}
          pageMetadataEnabled={canViewUserEdits}
          canEditPageMetadata={canEditUserEdits}
          includeInternalIds={isGeckoUser}
          onMetadataEditingChange={handleMetadataEditingChange}
          onSwitchPreviewDocument={onSwitchPreviewDocument}
          onOpenDocumentInNewTab={handleOpenDocumentInNewTab}
        />
      </Box>

      {!isMetadataEditing ? (
        <ActionFooter>
          <ActionButtons>
            <Button
              variant='primaryRaised'
              size='lg'
              onClick={() =>
                onDocumentNavigate(document, false, isLinkedDocsMode, {
                  pageNumber: initialPage,
                  citedQuotes,
                })
              }>
              <Trans>Open</Trans>
            </Button>
            <Button
              variant='inputRaised'
              size='lg'
              onClick={() =>
                onDocumentNavigate(document, true, isLinkedDocsMode, {
                  pageNumber: initialPage,
                  citedQuotes,
                })
              }
              title={t`Open in new tab`}>
              <Trans>Open In New Tab</Trans>
              <ExternalLink />
            </Button>
          </ActionButtons>
        </ActionFooter>
      ) : null}
    </PanelRoot>
  )
}
